import { ComponentProps, FC } from "react";
import { Row, Col, Tabs, Typography, Space, Grid } from "antd";
import moment from "moment";
import * as _ from "lodash";

import { classname } from "@ctra/utils";
import styles from "./DateRangePicker.module.less";

const { Text } = Typography;

export interface DateRangePickerProps extends Omit<ComponentProps<typeof Row>, "onChange"> {
  /**
   * center date to manipulate
   */
  center: moment.Moment;
  /**
   * list of ISO time periods
   */
  durations: Array<string> | Array<{ duration: string; label: string }>;
  /**
   * current value
   */
  value: string;
  /**
   * change handler
   */
  onChange: (period: string) => void;
  /**
   * labels
   */
  labels: {
    before: string;
    after: string;
  };
}

/**
 * Date range picker component
 * @param {Array<string> | Array<{duration: string, label: string}>} durations
 * @param {{(period: string): void}} onChange
 * @param {moment.Moment} center
 * @param {string} value
 * @param {{before: string, after: string}} labels
 * @param {string | undefined} className
 * @param {Pick<DateRangePickerProps & {children?: React.ReactNode | undefined}, "gutter" | "align" | "justify" | "prefixCls" | "wrap" | "defaultChecked" | "defaultValue" | "suppressContentEditableWarning" | "suppressHydrationWarning" | "accessKey" | "contentEditable" | "contextMenu" | "dir" | "draggable" | "hidden" | "id" | "lang" | "placeholder" | "slot" | "spellCheck" | "style" | "tabIndex" | "title" | "translate" | "radioGroup" | "role" | "about" | "datatype" | "inlist" | "prefix" | "property" | "resource" | "typeof" | "vocab" | "autoCapitalize" | "autoCorrect" | "autoSave" | "color" | "itemProp" | "itemScope" | "itemType" | "itemID" | "itemRef" | "results" | "security" | "unselectable" | "inputMode" | "is" | "aria-activedescendant" | "aria-atomic" | "aria-autocomplete" | "aria-busy" | "aria-checked" | "aria-colcount" | "aria-colindex" | "aria-colspan" | "aria-controls" | "aria-current" | "aria-describedby" | "aria-details" | "aria-disabled" | "aria-dropeffect" | "aria-errormessage" | "aria-expanded" | "aria-flowto" | "aria-grabbed" | "aria-haspopup" | "aria-hidden" | "aria-invalid" | "aria-keyshortcuts" | "aria-label" | "aria-labelledby" | "aria-level" | "aria-live" | "aria-modal" | "aria-multiline" | "aria-multiselectable" | "aria-orientation" | "aria-owns" | "aria-placeholder" | "aria-posinset" | "aria-pressed" | "aria-readonly" | "aria-relevant" | "aria-required" | "aria-roledescription" | "aria-rowcount" | "aria-rowindex" | "aria-rowspan" | "aria-selected" | "aria-setsize" | "aria-sort" | "aria-valuemax" | "aria-valuemin" | "aria-valuenow" | "aria-valuetext" | "children" | "dangerouslySetInnerHTML" | "onCopy" | "onCopyCapture" | "onCut" | "onCutCapture" | "onPaste" | "onPasteCapture" | "onCompositionEnd" | "onCompositionEndCapture" | "onCompositionStart" | "onCompositionStartCapture" | "onCompositionUpdate" | "onCompositionUpdateCapture" | "onFocus" | "onFocusCapture" | "onBlur" | "onBlurCapture" | "onChangeCapture" | "onBeforeInput" | "onBeforeInputCapture" | "onInput" | "onInputCapture" | "onReset" | "onResetCapture" | "onSubmit" | "onSubmitCapture" | "onInvalid" | "onInvalidCapture" | "onLoad" | "onLoadCapture" | "onError" | "onErrorCapture" | "onKeyDown" | "onKeyDownCapture" | "onKeyPress" | "onKeyPressCapture" | "onKeyUp" | "onKeyUpCapture" | "onAbort" | "onAbortCapture" | "onCanPlay" | "onCanPlayCapture" | "onCanPlayThrough" | "onCanPlayThroughCapture" | "onDurationChange" | "onDurationChangeCapture" | "onEmptied" | "onEmptiedCapture" | "onEncrypted" | "onEncryptedCapture" | "onEnded" | "onEndedCapture" | "onLoadedData" | "onLoadedDataCapture" | "onLoadedMetadata" | "onLoadedMetadataCapture" | "onLoadStart" | "onLoadStartCapture" | "onPause" | "onPauseCapture" | "onPlay" | "onPlayCapture" | "onPlaying" | "onPlayingCapture" | "onProgress" | "onProgressCapture" | "onRateChange" | "onRateChangeCapture" | "onSeeked" | "onSeekedCapture" | "onSeeking" | "onSeekingCapture" | "onStalled" | "onStalledCapture" | "onSuspend" | "onSuspendCapture" | "onTimeUpdate" | "onTimeUpdateCapture" | "onVolumeChange" | "onVolumeChangeCapture" | "onWaiting" | "onWaitingCapture" | "onAuxClick" | "onAuxClickCapture" | "onClick" | "onClickCapture" | "onContextMenu" | "onContextMenuCapture" | "onDoubleClick" | "onDoubleClickCapture" | "onDrag" | "onDragCapture" | "onDragEnd" | "onDragEndCapture" | "onDragEnter" | "onDragEnterCapture" | "onDragExit" | "onDragExitCapture" | "onDragLeave" | "onDragLeaveCapture" | "onDragOver" | "onDragOverCapture" | "onDragStart" | "onDragStartCapture" | "onDrop" | "onDropCapture" | "onMouseDown" | "onMouseDownCapture" | "onMouseEnter" | "onMouseLeave" | "onMouseMove" | "onMouseMoveCapture" | "onMouseOut" | "onMouseOutCapture" | "onMouseOver" | "onMouseOverCapture" | "onMouseUp" | "onMouseUpCapture" | "onSelect" | "onSelectCapture" | "onTouchCancel" | "onTouchCancelCapture" | "onTouchEnd" | "onTouchEndCapture" | "onTouchMove" | "onTouchMoveCapture" | "onTouchStart" | "onTouchStartCapture" | "onPointerDown" | "onPointerDownCapture" | "onPointerMove" | "onPointerMoveCapture" | "onPointerUp" | "onPointerUpCapture" | "onPointerCancel" | "onPointerCancelCapture" | "onPointerEnter" | "onPointerEnterCapture" | "onPointerLeave" | "onPointerLeaveCapture" | "onPointerOver" | "onPointerOverCapture" | "onPointerOut" | "onPointerOutCapture" | "onGotPointerCapture" | "onGotPointerCaptureCapture" | "onLostPointerCapture" | "onLostPointerCaptureCapture" | "onScroll" | "onScrollCapture" | "onWheel" | "onWheelCapture" | "onAnimationStart" | "onAnimationStartCapture" | "onAnimationEnd" | "onAnimationEndCapture" | "onAnimationIteration" | "onAnimationIterationCapture" | "onTransitionEnd" | "onTransitionEndCapture" | "ref" | "key">} rest
 * @return {JSX.Element}
 * @constructor
 */
const DateRangePicker: FC<DateRangePickerProps> = ({
  durations,
  onChange,
  center,
  value,
  labels,
  className,
  ...rest
}) => {
  const valueDuration = moment.duration(value);
  const { md } = Grid.useBreakpoint();

  /**
   * Start and end labels
   */
  const [startLabel, endLabel] = [
    center.clone().subtract(valueDuration).format("LL"),
    center.clone().add(valueDuration).format("LL")
  ];

  /**
   * Center label
   * @type {string}
   */
  const centerLabel = center.format("LL");

  /**
   * Make tabs
   * @type {{key: string, label: string}[]}
   */
  const tabs = _.map<typeof durations[number], { key: string; label: string }>(durations, (duration) => {
    if (typeof duration === "string") {
      return {
        key: duration,
        label: moment.duration(duration).humanize()
      };
    } else {
      const { duration: durationString, label } = duration;

      return {
        key: durationString,
        label
      };
    }
  });

  return (
    <Row
      gutter={[16, md ? 0 : 4]}
      align="middle"
      className={classname("ctra-impactTracking-dateRangePicker", styles.DateRangePicker, className)}
      {...rest}
    >
      <Col {...(md ? {} : { span: 24 })}>
        <div className={styles.DateRange}>
          <Space>
            <Text className={styles.Label}>{labels.before}</Text>
            <Text className={styles.Date}>
              {startLabel} - {centerLabel}
            </Text>
          </Space>
        </div>
      </Col>
      <Col {...(md ? {} : { span: 24 })}>
        <div className={styles.DateRange}>
          <Space>
            <Text className={styles.Label}>{labels.after}</Text>
            <Text className={styles.Date}>
              {centerLabel} - {endLabel}
            </Text>
          </Space>
        </div>
      </Col>
      <Col className={styles.Tabs} {...(md ? {} : { span: 24 })}>
        <Tabs activeKey={value} items={tabs} onChange={onChange} />
      </Col>
    </Row>
  );
};

export default DateRangePicker;
